import {useLazyQuery} from "@apollo/client";
import classNames from "classnames";
import {parseISO} from "date-fns";
import {useState} from "react";

import DefaultLogo from "@/assets/images/integrations/default.png";
import {Button} from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {TwoLineShortDateTimeUser} from "@/components/datetime/TwoLineShortDateTimeUser.tsx";
import {dateStringWithTimezone, timeStringWithTimezone} from "@/components/datetime/utils.ts";
import {ActionDialog} from "@/components/overlay/ActionDialog.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {StatusPill} from "@/components/statusPill/StatusPill.tsx";
import {PillStatus} from "@/components/statusPill/StatusPill.types.ts";
import {Table} from "@/components/table/Table.tsx";
import {GQL_INTEGRATION_ERROR_QUERY, IntegrationErrorQueryResponse} from "@/lib/queries/integrations.ts";
import {IntegrationError, IntegrationListData} from "@/lib/types/integrations.ts";
import {getIntegrationModuleInfo} from "@/pages/integrations/integrations.types.ts";

import styles from "./IntegrationLogs.module.scss";

const EVENT_PILL_MAP: Record<string, PillStatus> = {
  alert_raised: PillStatus.INTEGRATION_EVENT_ALERT_RAISED,
  alert_cleared: PillStatus.INTEGRATION_EVENT_ALERT_CLEARED,
  periodic: PillStatus.INTEGRATION_EVENT_PERIODIC,
  periodic_raw: PillStatus.INTEGRATION_EVENT_PERIODIC_RAW,
  reconfigured: PillStatus.INTEGRATION_EVENT_RECONFIGURED,
  timeseries: PillStatus.INTEGRATION_EVENT_TIMESERIES,
  test: PillStatus.INTEGRATION_EVENT_TEST,
};

const IntegrationLogTable = ({integration}: {integration: IntegrationListData}) => {
  const {user: currentUser} = useCurrentUserContext();
  const [viewedError, setViewedError] = useState<IntegrationError | null>(null);
  const [loadFullError, {data: fullErrorData, loading: fullErrorLoading}] =
    useLazyQuery<IntegrationErrorQueryResponse>(GQL_INTEGRATION_ERROR_QUERY);

  const moduleInfo = getIntegrationModuleInfo(integration.module);

  const viewFullError = (error: IntegrationError) => {
    setViewedError(error);
    loadFullError({variables: {id: error.id}});
  };

  const fullErrorTitle = (error: IntegrationError) => {
    const errorDate = parseISO(error.created_at.toString());
    return (
      <span className={styles.fullErrorTitle}>
        <img
          src={moduleInfo?.logo ?? DefaultLogo}
          alt={moduleInfo?.label ?? integration.module}
          height={20}
          width={20}
          className="pulse-border-radius-lg"
        />
        {integration.name}:{" "}
        {dateStringWithTimezone(errorDate, currentUser!.account.timezone, currentUser!.current_date_format)}{" "}
        {timeStringWithTimezone(errorDate, currentUser!.account.timezone, currentUser!.current_time_format)}
      </span>
    );
  };

  return (
    <>
      <Table
        minWidth="600px"
        specs={[
          {
            fieldName: "created_at",
            fieldTitle: "Date",
            meta: {
              headerClassName: "w-20",
            },
            accessor: (error) => <TwoLineShortDateTimeUser date={error.created_at.toString()} user={currentUser!} />,
          },
          {
            fieldName: "event",
            fieldTitle: "Event",
            meta: {
              headerClassName: "w-20",
            },
            accessor: (error) =>
              EVENT_PILL_MAP[error.event] ? (
                <StatusPill type="incident" variant="textAndIcon" status={EVENT_PILL_MAP[error.event]} />
              ) : (
                error.event
              ),
          },
          {
            fieldName: "short_error",
            fieldTitle: "Error",
            meta: {
              headerClassName: "w-60",
              columnClassName: classNames(styles.errorCell),
            },
            accessor: (error) => (
              <>
                <div>{error.short_error}</div>
                <Button variant="linkBlue" size="text" onClick={() => viewFullError(error)}>
                  View Full Error
                </Button>
              </>
            ),
          },
        ]}
        rows={integration.errors}
      />
      <ActionDialog
        show={!!viewedError}
        title={viewedError && fullErrorTitle(viewedError)}
        size="lg"
        contentClassName={styles.fullErrorContent}
        bodyClassName={styles.fullErrorBody}
        onCancel={() => setViewedError(null)}
        onConfirm={() => setViewedError(null)}
        hasCancelButton={false}
        confirmBtnText="Close"
      >
        {fullErrorLoading ? (
          <LoadingContainer />
        ) : (
          <pre className={styles.fullError}>
            {fullErrorData?.integrationError?.full_error ?? viewedError?.short_error}
          </pre>
        )}
      </ActionDialog>
    </>
  );
};

const EmptyLogView = () => {
  return <div>No errors logged</div>;
};

type IntegrationLogsProps = {
  integration: IntegrationListData;
};

export const IntegrationLogs = ({integration}: IntegrationLogsProps) => {
  return integration.errors.length > 0 ? <IntegrationLogTable integration={integration} /> : <EmptyLogView />;
};
